import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AdvancedFilterButton, FilterPanelCollapse } from '@/components/common/AdvancedFilter';
import { DataTable } from '@/components/common/DataTable';
import { DateRangeBadge } from '@/components/common/DateRangeBadge';
import { ErrorState } from '@/components/common/ErrorState';
import { StatCardGrid } from '@/components/common/StatCardGrid';
import { PageHeader } from '@/components/layout/PageHeader';
import { FIT_TO_SCREEN_PAGE_CLASS } from '@/components/layout/page.constants';
import { getItemCodeFilterField } from '@/features/promotion/utils/promotionFilterFields';
import { zonalManagersQueryOptions } from '@/features/users/api/users.api';
import { useInfiniteTableQuery } from '@/hooks/useInfiniteTableQuery';
import { getZonalManagerFilterField } from '@/utils/filterFields';
import { getUpcomingSearch } from '@/utils/upcomingSearch';
import { mpSummaryInfiniteQueryOptions } from '../api/marketPromotion.api';
import { MpAreaFilterPanel } from '../components/MpAreaFilterPanel';
import { getMpSummaryColumns } from '../components/MpColumns';
import { useMpSummaryFilters } from '../hooks/useMpReportFilters';
import { getBudgetStatCards } from '../utils/budgetStatCards';

const EXTRA_FILTER_FIELDS = [getItemCodeFilterField()];

export function MpDistributionSummaryPage() {
  const controller = useMpSummaryFilters();
  const { rows, firstPageMeta, isLoading, loadError, refetch, loadMoreProps } =
    useInfiniteTableQuery(mpSummaryInfiniteQueryOptions(controller.params));
  const { data: zonalManagers } = useQuery(zonalManagersQueryOptions());

  const [isFilterOpen, setIsFilterOpen] = useState(false);

  return (
    <div className={FIT_TO_SCREEN_PAGE_CLASS}>
      <PageHeader
        title="Distribution Summary"
        titleAddon={
          <DateRangeBadge dateFrom={controller.params.dateFrom} dateTo={controller.params.dateTo} />
        }
        search={getUpcomingSearch('Name, HR ID')}
        actions={
          <AdvancedFilterButton
            isOpen={isFilterOpen}
            activeCount={controller.activeFilterCount}
            onClick={() => setIsFilterOpen((wasOpen) => !wasOpen)}
          />
        }
      />

      <FilterPanelCollapse isOpen={isFilterOpen}>
        <MpAreaFilterPanel
          filters={controller}
          personField={getZonalManagerFilterField(zonalManagers)}
          extraFields={EXTRA_FILTER_FIELDS}
        />
      </FilterPanelCollapse>

      {loadError ? (
        <ErrorState
          error={loadError}
          fallbackTitle="Could not load the summary"
          onRetry={() => void refetch()}
          fill
        />
      ) : (
        <>
          <StatCardGrid isLoading={isLoading} stats={getBudgetStatCards(firstPageMeta?.totals)} />

          <DataTable
            data={rows}
            columns={getMpSummaryColumns()}
            isLoading={isLoading}
            className="lg:flex-1"
            stickyColumnId="name"
            emptyMessage="No budget matches"
            {...loadMoreProps}
          />
        </>
      )}
    </div>
  );
}
